import * as React from 'react';
import { ThemeProvider } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import { useNavigate } from 'react-router-dom';

import Navigator from './Navigator';
import Content, { ContentPropType } from './Content';
import Header from './Header';
import { theme } from './theme';
import { checkRole } from 'utils';
import { ROLE } from 'constant/role';

const expandedWidth = 236;
const collapsedWidth = 60;
const SIDEBAR_STORAGE_KEY = 'vschool_sidebar_open';

export default function MainLayout(
  props: ContentPropType & { expectRole?: ROLE | ROLE[] }
) {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [isNavOpen, setIsNavOpen] = React.useState<boolean>(() => {
    if (props.subNavKey) return false;
    return localStorage.getItem(SIDEBAR_STORAGE_KEY) !== 'false';
  });
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const navigate = useNavigate();

  const isLoggedIn = Boolean(localStorage.getItem('email'));
  const canAccess = props.expectRole ? checkRole(props.expectRole) : true;

  React.useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login');
    } else if (!canAccess) {
      navigate('/');
    }
  }, [navigate, canAccess, isLoggedIn]);

  // Auto-collapse sidebar on detail pages (when subNavKey exists),
  // otherwise restore user's saved preference
  React.useEffect(() => {
    if (props.subNavKey) {
      setIsNavOpen(false);
    } else {
      setIsNavOpen(localStorage.getItem(SIDEBAR_STORAGE_KEY) !== 'false');
    }
  }, [props.subNavKey]);

  const handleDrawerToggle = () => {
    if (isSmUp) {
      setIsNavOpen((prev) => {
        const next = !prev;
        localStorage.setItem(SIDEBAR_STORAGE_KEY, String(next));
        return next;
      });
    } else {
      setMobileOpen((prev) => !prev);
    }
  };

  if (!isLoggedIn) return null;

  const currentDrawerWidth = isNavOpen ? expandedWidth : collapsedWidth;

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: 'flex', minHeight: '100vh', width: '100%', maxWidth: '100vw' }}>
        <CssBaseline />
        <Box
          component="nav"
          sx={{
            width: { sm: currentDrawerWidth },
            flexShrink: { sm: 0 },
            overflowX: 'hidden',
            transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          {isSmUp ? null : (
            <Navigator
              PaperProps={{ style: { width: expandedWidth } }}
              variant="temporary"
              open={mobileOpen}
              onClose={handleDrawerToggle}
              collapsed={false}
            />
          )}
          <Navigator
            PaperProps={{
              sx: {
                width: currentDrawerWidth,
                overflowX: 'hidden',
                transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
              },
            }}
            sx={{ display: { sm: 'block', xs: 'none' } }}
            collapsed={!isNavOpen}
            onToggleCollapse={handleDrawerToggle}
          />
        </Box>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, maxWidth: '100%' }}>
          <Header onDrawerToggle={handleDrawerToggle} />
          <Box component="main" sx={{ flex: 1, bgcolor: '#ffffff', minWidth: 0, maxWidth: '100%' }}>
            {canAccess ? <Content {...props} /> : null}
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
